import * as React from "react";
import clsx from "clsx";
import type { Language } from "../../../app/lib/api/localization";
import { formatContentKeyDisplay } from "../../../app/lib/i18n/contentKeys";
import { useI18n } from "../../../app/lib/i18n";
import { inputChanged } from "../Form/FormInput.style.css";
import * as s from "./LocalizedField.style.css";

export interface LocalizedFieldProps {
  label: string;
  locKey: string;
  languages: Language[];
  /** Active language for this field. Defaults to the default language. */
  selectedLang?: string;
  onSelectedLangChange?: (code: string) => void;
  /** Value for the currently selected language. */
  value: string;
  onChange: (lang: string, value: string) => void;
  /**
   * Default-language string for this key. Shown in a popup hint above the
   * field while focused when editing a non-default language.
   */
  referenceValue?: string;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  defaultLang?: string;
  /** Highlight the control when it has unsaved edits. */
  changed?: boolean;
  /**
   * Increment to replay the remote-update flash animation (e.g. after a
   * websocket merge into a clean field).
   */
  flashKey?: number;
  /** Called when the user switches language (e.g. to lazy-load definitions). */
  onLanguageFocus?: (code: string) => void;
}

export function LocalizedField({
  label,
  locKey,
  languages,
  selectedLang: controlledLang,
  onSelectedLangChange,
  value,
  onChange,
  referenceValue,
  multiline = false,
  rows = 4,
  placeholder,
  disabled,
  required,
  autoFocus,
  defaultLang = "en",
  changed,
  flashKey = 0,
  onLanguageFocus,
}: LocalizedFieldProps) {
  const { t } = useI18n();
  const fallbackLang =
    languages.find((l) => l.isDefault)?.code ??
    languages.find((l) => l.code === defaultLang)?.code ??
    languages[0]?.code ??
    defaultLang;

  const [internalLang, setInternalLang] = React.useState(fallbackLang);
  const selectedLang = controlledLang ?? internalLang;
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [valueFocused, setValueFocused] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (controlledLang == null && !languages.some((l) => l.code === internalLang)) {
      setInternalLang(fallbackLang);
    }
  }, [controlledLang, languages, internalLang, fallbackLang]);

  const current =
    languages.find((l) => l.code === selectedLang) ??
    languages.find((l) => l.isDefault) ??
    languages[0] ??
    null;

  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const setLang = (code: string) => {
    if (controlledLang == null) setInternalLang(code);
    onSelectedLangChange?.(code);
    onLanguageFocus?.(code);
    setMenuOpen(false);
  };

  const showReferenceHint =
    valueFocused &&
    !disabled &&
    selectedLang !== fallbackLang &&
    !!referenceValue?.trim();

  return (
    <div className={s.root}>
      <div className={s.header}>
        <span className={s.label}>
          {label}
          {required ? " *" : ""}
        </span>
        <div className={s.meta}>
          <span
            className={s.keyChip}
            title={`Localized string key:\n${locKey}`}
          >
            {formatContentKeyDisplay(locKey)}
          </span>
          <div className={s.menuWrap} ref={wrapRef}>
            <button
              type="button"
              className={s.flagBtn}
              disabled={disabled || languages.length === 0}
              aria-label={`Language for this field: ${current?.name ?? selectedLang}`}
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
              title={current ? `${current.name} (${current.code})` : selectedLang}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {current?.flagUrl ? (
                <img src={current.flagUrl} alt="" className={s.flagImg} />
              ) : (
                <span className={s.flagCode}>{current?.code ?? selectedLang}</span>
              )}
            </button>
            {menuOpen ? (
              <div className={s.menu} role="listbox">
                {languages.map((lang) => (
                  <button
                    key={lang.id}
                    type="button"
                    role="option"
                    aria-selected={lang.code === selectedLang}
                    className={`${s.menuItem}${lang.code === selectedLang ? ` ${s.menuItemActive}` : ""}`}
                    onClick={() => setLang(lang.code)}
                  >
                    {lang.flagUrl ? (
                      <img src={lang.flagUrl} alt="" className={s.flagImg} />
                    ) : (
                      <span className={s.flagCode}>{lang.code}</span>
                    )}
                    <span>
                      {lang.name} ({lang.code})
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className={s.valueFieldWrap}>
        {multiline ? (
          <textarea
            key={flashKey > 0 ? `flash-${flashKey}` : undefined}
            className={clsx(
              s.textarea,
              changed && inputChanged,
              flashKey > 0 && s.remoteFlash,
            )}
            value={value}
            onChange={(e) => onChange(selectedLang, e.target.value)}
            disabled={disabled}
            rows={rows}
            placeholder={placeholder}
            autoFocus={autoFocus}
            onFocus={() => setValueFocused(true)}
            onBlur={() => setValueFocused(false)}
            data-unsaved={changed ? "true" : undefined}
          />
        ) : (
          <input
            key={flashKey > 0 ? `flash-${flashKey}` : undefined}
            type="text"
            className={clsx(
              s.input,
              changed && inputChanged,
              flashKey > 0 && s.remoteFlash,
            )}
            value={value}
            onChange={(e) => onChange(selectedLang, e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            autoFocus={autoFocus}
            onFocus={() => setValueFocused(true)}
            onBlur={() => setValueFocused(false)}
            data-unsaved={changed ? "true" : undefined}
          />
        )}
        {showReferenceHint ? (
          <div className={s.valueHint} role="tooltip">
            <div className={s.valueHintLabel}>{t("shared.localizedField.default")}</div>
            <div className={s.valueHintText}>{referenceValue}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
